import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Divider,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Tooltip,
  Button,
  TextField,
  MenuItem,
} from '@mui/material';
import { Close, AccountTree, OpenInNew, Delete, Add, TableChart } from '@mui/icons-material';
import { extractErrorMessage } from '../../../utils/errorUtils';
import areaService from '../services/areaService';
import correspondenceTypeService from '../../correspondence-types/services/correspondenceTypeService';
import retentionService from '../../retentions/services/retentionService';
import axiosInstance from '../../../api/axiosConfig';

const AreaDetailModal = ({ open, onClose, areaId }) => {
  const navigate = useNavigate();
  const [area, setArea] = useState(null);
  const [correspondenceTypes, setCorrespondenceTypes] = useState([]);
  const [retentions, setRetentions] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [addUserId, setAddUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && areaId) {
      loadData();
    }
  }, [open, areaId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [areaRes, ctData, retentionData] = await Promise.all([
        areaService.getById(areaId),
        correspondenceTypeService.getAll({ areaId, limit: 100 }),
        retentionService.getAll({ areaId, limit: 100 }),
      ]);
      const areaData = areaRes.data || areaRes;
      setArea(areaData);
      setCorrespondenceTypes(ctData.data || []);
      setRetentions(retentionData.data || []);

      // Load company users for add selector
      const cId = areaData?.companyId || areaData?.company?.id;
      if (cId) {
        const usersRes = await axiosInstance.get('/users', {
          params: { companyId: String(cId), limit: 200 },
        }).catch(() => ({ data: { data: [] } }));
        setCompanyUsers(usersRes.data?.data || []);
      }
    } catch (err) {
      setError('Error al cargar los datos del área');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!addUserId) return;
    setSubmitting(true);
    try {
      await areaService.assignUsers(areaId, [parseInt(addUserId)]);
      setAddUserId('');
      await loadData();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    setSubmitting(true);
    try {
      await areaService.removeUser(areaId, userId);
      await loadData();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const users = area?.areaUsers?.map((au) => au.user) || [];
  const assignedUserIds = new Set(users.map((u) => String(u.id)));
  const availableUsers = companyUsers.filter((u) => !assignedUserIds.has(String(u.id)));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <AccountTree color="primary" />
        <Typography variant="h6" component="span">
          {area?.name || 'Detalle del Área'}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && !error && area && (
          <>
            {/* Información general */}
            <Typography variant="overline" color="text.secondary" fontWeight="bold">
              Información General
            </Typography>
            <Box sx={{ mt: 1, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Código</Typography>
                <Typography variant="body2" fontWeight="medium">{area.code || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Empresa</Typography>
                <Typography variant="body2" fontWeight="medium">{area.company?.name || '—'}</Typography>
              </Box>
              {area.description && (
                <Box sx={{ width: '100%' }}>
                  <Typography variant="caption" color="text.secondary">Descripción</Typography>
                  <Typography variant="body2">{area.description}</Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Tipos de correspondencia */}
            <Typography variant="overline" color="text.secondary" fontWeight="bold">
              Tipos de Correspondencia ({correspondenceTypes.length})
            </Typography>
            <Box sx={{ mt: 1, mb: 2 }}>
              {correspondenceTypes.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  No hay tipos de correspondencia asociados
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {correspondenceTypes.map((ct) => (
                    <Box key={ct.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={ct.name} size="small" color="primary" variant="outlined" />
                      {ct.responseDeadlineDays != null && (
                        <Typography variant="caption" color="text.secondary">
                          Vence: {ct.responseDeadlineDays} días
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Tablas de retención (TRD) */}
            <Typography variant="overline" color="text.secondary" fontWeight="bold">
              Tablas de Retención (TRD) ({retentions.length})
            </Typography>
            <Box sx={{ mt: 1, mb: 2 }}>
              {retentions.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  No hay tablas de retención asociadas
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {retentions.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((r) => (
                    <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TableChart fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight="medium">{r.name || r.code}</Typography>
                      {r.type && (
                        <Chip label={r.type} size="small" variant="outlined" />
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Usuarios */}
            <Typography variant="overline" color="text.secondary" fontWeight="bold">
              Usuarios ({users.length})
            </Typography>
            <Box sx={{ mt: 1 }}>
              {users.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  No hay usuarios asignados a esta área
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {users.map((user) => (
                    <Box key={user.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                        {user.name?.[0]?.toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight="medium" noWrap>
                          {user.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {user.email}
                        </Typography>
                      </Box>
                      {user.role && (
                        <Chip label={user.role.name} size="small" variant="outlined" />
                      )}
                      <Tooltip title="Ver usuario">
                        <IconButton size="small" onClick={() => { onClose(); navigate(`/users/${user.id}`); }}>
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover del área">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveUser(user.id)}
                          disabled={submitting}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Agregar usuario */}
              {availableUsers.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <TextField
                    select
                    size="small"
                    label="Agregar Usuario"
                    value={addUserId}
                    onChange={(e) => setAddUserId(e.target.value)}
                    sx={{ flex: 1 }}
                  >
                    <MenuItem value="">Seleccionar...</MenuItem>
                    {availableUsers.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.name} — {u.email}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add />}
                    onClick={handleAddUser}
                    disabled={!addUserId || submitting}
                  >
                    Agregar
                  </Button>
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AreaDetailModal;
